import { asRecord, num, records, text } from './resultValues.js';

export type ChangeAction = 'Add' | 'Replace' | 'Remove';

export interface RecommendedChange {
  action: ChangeAction;
  element: string;
  current: string;
  proposed: string;
}

export interface CreativeRecommendation {
  id: string;
  title: string;
  rationale: string;
  delta: number;
  changes: RecommendedChange[];
}

export interface BestPracticeTip {
  feature: string;
  tag: string;
  attribute: string;
  abcd: string | null;
  score: number | null;
  text: string;
}

const ACTIONS: ChangeAction[] = ['Add', 'Replace', 'Remove'];

const NOT_PRESENT = 'Not present';

function actionOf(value: unknown): ChangeAction {
  return ACTIONS.find((action) => action === value) ?? 'Replace';
}

function scoredRow(
  result: unknown,
  creativeId: string,
  networkId: number,
): Record<string, unknown> | null {
  return (
    records(asRecord(result)?.['creatives']).find(
      (row) => text(row['creative_id']) === creativeId && num(row['network_id']) === networkId,
    ) ?? null
  );
}

export function creativeRecommendations(
  result: unknown,
  creativeId: string,
  networkId: number,
): CreativeRecommendation[] {
  const row = scoredRow(result, creativeId, networkId);
  if (row === null) return [];
  return records(row['recommendations']).flatMap((entry) => {
    const title = text(entry['title']);
    if (title === null) return [];
    return [
      {
        id: String(entry['id'] ?? title),
        title,
        rationale: text(entry['rationale']) ?? '',
        delta: num(entry['projected_final_delta']) ?? 0,
        changes: records(entry['tag_changes']).map((change) => ({
          action: actionOf(change['action']),
          element: [text(change['attribute']), text(change['feature'])]
            .filter((part) => part !== null)
            .join(' — '),
          current: text(change['current_tag']) ?? NOT_PRESENT,
          proposed: text(change['proposed_tag']) ?? NOT_PRESENT,
        })),
      },
    ];
  });
}

export function bestPracticeTips(
  result: unknown,
  creativeId: string,
  networkId: number,
): BestPracticeTip[] {
  const row = scoredRow(result, creativeId, networkId);
  if (row === null) return [];
  return records(row['features']).flatMap((entry) => {
    const said = text(entry['recommendation']);
    const feature = text(entry['feature']);
    if (said === null || feature === null) return [];
    return [
      {
        feature,
        tag: text(entry['tag']) ?? '',
        attribute: text(entry['attribute']) ?? '',
        abcd: text(entry['abcd_methodology']),
        score: num(entry['final_feature_score']),
        text: said,
      },
    ];
  });
}
